import { useState, useEffect } from 'react';
import { PrimeIndemnite } from '../types';
import { getSalaireHopitalPrimes, validatePrimeData } from '../services/primeService';
import { DEFAULT_PRIMES } from '../constants/primes';

/**
 * @fileoverview Hook for managing salaire_hopital prime data from Supabase
 * 
 * This hook provides a clean interface for components to access prime data
 * with proper loading states, error handling, and fallback to hardcoded data.
 * 
 * @author App'Ines Team
 * @version 1.0.0
 * @since 2025
 */

interface UsePrimesDataResult {
  primes: PrimeIndemnite[];
  isLoadingPrimes: boolean;
  setIsLoadingPrimes: (loading: boolean) => void;
  errorPrimes: string | null;
  isUsingFallbackPrimes: boolean;
  refetchPrimes: () => Promise<void>;
}

/**
 * Custom hook for fetching and managing salaire_hopital prime data
 * 
 * This hook handles the complete lifecycle of prime data:
 * - Fetches data from Supabase on mount
 * - Provides loading and error states
 * - Falls back to hardcoded data if Supabase fails
 * - Validates data integrity
 * - Provides a refetch function for manual refresh
 * 
 * @returns {UsePrimesDataResult} Object containing primes data and state
 * 
 * @example
 * function MyComponent() {
 *   const { primes, isLoadingPrimes, errorPrimes, isUsingFallbackPrimes } = usePrimesData();
 *   
 *   if (isLoadingPrimes) return <LoadingSpinner />;
 *   if (errorPrimes) return <ErrorMessage error={errorPrimes} />;
 *   
 *   return <PrimesList primes={primes} />;
 * }
 */
export function usePrimesData(): UsePrimesDataResult {
  const [primes, setPrimes] = useState<PrimeIndemnite[]>([]);
  const [isLoadingPrimes, setIsLoadingPrimes] = useState(true);
  const [errorPrimes, setErrorPrimes] = useState<string | null>(null);
  const [isUsingFallbackPrimes, setIsUsingFallbackPrimes] = useState(false);

  const fetchData = async () => {
    try {
      setIsLoadingPrimes(true);
      setErrorPrimes(null);
      setIsUsingFallbackPrimes(false);

      console.log('Fetching salaire_hopital prime data from Supabase...');
      const supabasePrimes = await getSalaireHopitalPrimes();
      
      // Validate the fetched data
      if (!validatePrimeData(supabasePrimes)) {
        console.warn('Invalid prime data structure received from Supabase. Falling back to hardcoded data.');
        setPrimes(DEFAULT_PRIMES);
        setIsUsingFallbackPrimes(true);
        setErrorPrimes('Invalid prime data structure received from Supabase. Using fallback data.');
        setIsLoadingPrimes(false);
        return;
      }

      setPrimes(supabasePrimes);
      console.log('Successfully loaded prime data from Supabase');
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      console.error('Failed to fetch prime data from Supabase:', errorMessage);
      
      // Fallback to hardcoded data
      console.log('Falling back to hardcoded prime data');
      setPrimes(DEFAULT_PRIMES);
      setIsUsingFallbackPrimes(true);
      setErrorPrimes(`Supabase connection failed: ${errorMessage}. Using fallback data.`);
      
    } finally {
      setIsLoadingPrimes(false);
    }
  };

  // Fetch data on mount
  useEffect(() => {
    fetchData();
  }, []);

  const refetchPrimes = async () => {
    await fetchData();
  };

  return {
    primes,
    isLoadingPrimes,
    setIsLoadingPrimes,
    errorPrimes,
    isUsingFallbackPrimes,
    refetchPrimes
  };
}